import { css } from '@emotion/react';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { IconButtonComponent } from '~/_shared/baseComponents/buttons';
import { GoogleMapComponent } from '~/_shared/components/google-map/google-map.component';
import type { GoogleMapInstance } from '~/_shared/components/google-map/useGetGoogleMapInstance.hook';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import { useMarkerNameResolver } from '~/_shared/utils/spreadsheet/useMarkerNameResolver';
import { EMPTY_LOCATION_DESCRIPTION } from '~/customizeLocationPanel/customizeLocationPanel.constants';
import {
  getLocationDescription, getLocationDescriptionProperties,
} from '~/customizeLocationPanel/customizeLocationPanel.helpers';
import { convertPanelSettingsStateToPanelSettings } from '~/customizeLocationPanel/locationData/panelSettings.helpers';
import { useBubbleItemsAndOrder } from '~/customizeLocationPanel/locationData/useBubbleItemsAndOrder.hook';
import { usePanelSettingsStateSelector } from '~/customizeLocationPanel/locationData/usePanelSettingsState.selector';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { ReportMapSummaryComponent } from '~/report/mapSummary/reportMapSummary.component';
import { createRadiusFilterItemRequest } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import { useSpreadsheetRowData } from '~/spreadsheet/useSpreadsheetRowData';
import type { BoundaryGroupDemographic } from '~/store/boundaryGroups/boundaryGroups.repository';
import {
  type DemographicsResult, getDemographicsQuery,
} from '~/store/demographics/demographics.repository';
import { useDemographicsListGlobalRegionsSelector } from '~/store/demographics/demographicsListGlobal/demographicsListGlobal.selectors';
import { exportReportDisable } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.actionCreators';
import type { ExportReportLocationParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';
import { useMatchupDataSelector } from '~/store/matchupData/matchupData.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

type MapSummaryLocationProps = Readonly<{
  params: ExportReportLocationParams;
}>;

type CategoryId = string;

export const groupMetricsByCategory = (
  globalDemographics: readonly BoundaryGroupDemographic[],
  responses: readonly DemographicsResult[]
): GroupedItems[] => {
  const results: GroupedItems[] = [];

  const categories = globalDemographics.reduce<Map<CategoryId, BoundaryGroupDemographic[]>>((acc, demographic) => {
    const demographicItems = acc.get(demographic.category) ?? [];
    demographicItems.push(demographic);
    acc.set(demographic.category, demographicItems);

    return acc;
  }, new Map());

  const radius1Map = new Map<string, DemographicsResult>();
  const radius2Map = new Map<string, DemographicsResult>();
  const radius3Map = new Map<string, DemographicsResult>();
  responses.forEach((r) => radius1Map.set(r.demographicId, r));

  for (const [categoryName, demographics] of categories.entries()) {
    results.push({
      category: categoryName,
      groupedRows: demographics.map(demographic => ({
        name: demographic.name,
        radius1: radius1Map.get(demographic.id)?.value.toString() ?? '-',
        radius2: radius2Map.get(demographic.id)?.value.toString() ?? '-',
        radius3: radius3Map.get(demographic.id)?.value.toString() ?? '-',
      })),
    });
  }

  return results;
};

const wrapperStyle = css({
  padding: 20,
  maxWidth: 785,
  margin: '0 auto',
});

const mapsWrapperStyle = css({
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 12,
});

const googleMapStyle = css({
  width: 386,
  height: 234,
});

type GroupedRow = {
  name: string;
  radius1: string;
  radius2: string;
  radius3: string;
};

type GroupedItems = {
  category: string;
  groupedRows: GroupedRow[];
};

export const MapSummaryLocationComponent: FC<MapSummaryLocationProps> = (props) => {
  const [mapRoadWay, setMapRoadWay] = useState<GoogleMapInstance>();
  const [mapHybrid, setMapHybrid] = useState<GoogleMapInstance>();
  const [groupedItems, setGroupedItems] = useState<GroupedItems[]>([]);
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const clientId = useClientIdSelector();
  const globalDemographics = useDemographicsListGlobalRegionsSelector();
  const matchupData = useMatchupDataSelector();
  const { markerNameResolver } = useMarkerNameResolver();

  const spreadsheetData = useLatLngSpreadsheetData();
  const theme = useTheme();
  const panelSettingsState = usePanelSettingsStateSelector();

  const panelSettings = useMemo(() => (
    convertPanelSettingsStateToPanelSettings(panelSettingsState, theme)
  ), [panelSettingsState, theme]);

  const rowData = spreadsheetData.getRow(props.params.data.markerId);
  const { oneRow: spreadsheetRowData } = useSpreadsheetRowData(rowData);

  // TODO: adjust this to use much less data, no bubbleItems are needed
  const bubbleRecords = useBubbleItemsAndOrder({ spreadsheetRowId: rowData, spreadsheetRowData: spreadsheetRowData?.rowData, panelSettings });

  const closePdfSummary = () => {
    dispatch(exportReportDisable());
  };

  const handleMapInit = useCallback((map: GoogleMapInstance) => {
    setMapRoadWay(map);

    setTimeout(() => {
      (window as any).isReportFullyLoaded = true;
    }, 1000);
  }, []);

  const handleMapInit2 = useCallback((map: GoogleMapInstance) => {
    setMapHybrid(map);

    setTimeout(() => {
      (window as any).isReportFullyLoaded = true;
    }, 1000);
  }, []);

  useEffect(() => {
    if (!mapRoadWay) {
      return;
    }

    mapRoadWay.instance.setOptions({
      center: {
        lat: 28.3366342,
        lng: -81.6377178,
      },
      zoom: 15,
      disableDefaultUI: true,
    });
  }, [mapRoadWay]);

  useEffect(() => {
    if (!mapHybrid) {
      return;
    }

    mapHybrid.instance.setOptions({
      center: {
        lat: 28.3366342,
        lng: -81.6377178,
      },
      mapTypeId: 'hybrid',
      zoom: 15,
      disableDefaultUI: true,
    });
  }, [mapHybrid]);

  const summaryData = useMemo(() => {
    if (rowData && bubbleRecords) {
      const locationDescriptionProperties = getLocationDescriptionProperties({
        spreadsheetRowId: rowData,
        bubbleItems: bubbleRecords.items,
        matchupData,
      });

      return getLocationDescription({
        data: {
          ...locationDescriptionProperties,
          markerName: markerNameResolver(rowData.spreadsheetId, rowData.rowId, false),
        },
      });
    }

    return EMPTY_LOCATION_DESCRIPTION;
  }, [bubbleRecords, markerNameResolver, matchupData, rowData]);

  useEffect(() => {
    if (clientId === null || mapId === null || !rowData) {
      return;
    }
    const usDemographics = globalDemographics.find(item => item.id === 'US')?.demographics;
    if (!usDemographics || usDemographics.length === 0) {
      return;
    }

    getDemographicsQuery(clientId, {
      map_id: mapId,
      demographics_ids: usDemographics.map(demographic => demographic.id),
      radius_filter: {
        type: 'or',
        radiuses: [createRadiusFilterItemRequest({
          radius: 10,
          unit: UnitSystem.metric,
          center: {
            lat: rowData.lat,
            lng: rowData.lng,
          },
        })],
      },
    })
      .then(data => {
        setGroupedItems(groupMetricsByCategory(usDemographics, data));
      });
  }, [clientId, globalDemographics, mapId, rowData]);

  if (!rowData) {
    return null;
  }

  return (
    <div css={wrapperStyle}>
      <IconButtonComponent icon={faClose} onClick={closePdfSummary} />

      <ReportMapSummaryComponent
        latLng={{
          lat: rowData.lat,
          lng: rowData.lng,
        }}
        heading={summaryData.header}
        semiHeading={summaryData.semiHeader}
        description={summaryData.description}
      />

      <div css={mapsWrapperStyle}>
        <GoogleMapComponent
          css={googleMapStyle}
          onMapInitialized={handleMapInit}
          id="mapRoadway"
        />
        <GoogleMapComponent
          css={googleMapStyle}
          onMapInitialized={handleMapInit2}
          id="mapHybrid"
        />
      </div>

      <h2>SUMMARY TABLE</h2>

      <div>
        {groupedItems.map((item) => (
          <section key={item.category} css={{ marginBottom: '1.5rem' }}>
            <h2>{item.category}</h2>

            <ul style={{ paddingLeft: '1rem' }}>
              {item.groupedRows.map((row, index) => (
                <li key={index}>
                  {row.name}: <strong>{row.radius1}</strong> <strong>{row.radius2}</strong> <strong>{row.radius3}</strong>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};
